import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './DnsGraphs.scss';
import axios from 'axios';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { TableWidget } from '../table-widget/TableWidget';
import { useToast } from '../../services/ToastContext';
import CustomBreadcrumbs from '../breadcrumbs/Breadcrumbs';

type DnsComponentProps = {
  startDate?: string;
  endDate?: string;
  selectedDate?: string;
  deviceId?: any;
  cpeId?: any;
  setMarker?: any;
  url?: string;
  account?: string;
  clearFilter?: () => void;
};

// Router picker disabled to get page back Control + F ->  'need to go to get router select back' //

export const DnsGraphs = (props: DnsComponentProps) => {
  const { operatorId } = useParams();
  const { showToastMessage } = useToast();
  const { t, i18n } = useTranslation();

  // Series names / status values ('Normal', 'Warn', ...) stay in English as internal identifiers
  // (and server x-values); they are mapped to translated labels at render time with a raw fallback.
  const seriesLabels: Record<string, string> = {
    'DNS Requests': t('activity.series.dnsRequests'),
    'DNS Trend': t('activity.series.dnsTrend'),
    Alert: t('activity.series.alert'),
    Warn: t('activity.series.warn'),
    Normal: t('activity.series.normal'),
    Unknown: t('activity.series.unknown'),
    Routers: t('activity.series.routers'),
  };
  const seriesLabel = (name: any) => (typeof name === 'string' && seriesLabels[name]) || name;
  const localizeSeries = (series: any[]) => series.map((s: any) => (s && s.name ? { ...s, name: seriesLabel(s.name) } : s));
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const operatorNames = localStorage.getItem('operatorList');

  const operatorNameArray = operatorNames ? JSON.parse(operatorNames)?.find((item: { key: string }) => item.key === operatorId) : null;

  const [graphSwitch, setGraphSwitch] = useState<boolean>(true);

  const [dnsCheckBox, setDnsCheckBox] = useState<string>('DNS');
  const [requestersIds, setRequestersIds] = useState<any>([]);

  const [warnPercentage, setWarnPercentage] = useState<number>(40);
  const [alertPercentage, setAlertPercentage] = useState<number>(90);

  const [dnsDataClicked, setDnsDataClicked] = useState<boolean>(false);
  const [routerOptionClicked, setRouterOptionClicked] = useState<boolean>(false);
  const [routerClicked, setRouterClicked] = useState<boolean>(false);
  const [deviceChartClicked, setDeviceChartClicked] = useState<boolean>(false);
  const [urlClicked, setUrlClicked] = useState<boolean>(false);

  const [deviceSearch, setDeviceSearch] = useState<Number>();
  const [cpeSearch, setCpeSearch] = useState<number>();
  const [urlSearch, setUrlSearch] = useState<string>();

  const [dataClicked, setDataClicked] = useState<string>('');

  const [startDate, setStartDate] = useState<any>();
  const [endDate, setEndDate] = useState<any>();
  const [deviceIds, setDeviceIds] = useState<any>();
  const [chartDeviceIds, setChartDeviceIds] = useState<any>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const [currentText, setCurrentText] = useState(0);

  const [limit, setLimit] = useState<any>();

  useEffect(() => {
    setCpeSearch(Number(props.cpeId));
    setDeviceSearch(Number(props.deviceId));
    props.setMarker(false);
    setDnsDataClicked(false);
    setRouterOptionClicked(false);
    setRouterClicked(false);
    setDeviceChartClicked(false);
    setDataClicked('');
    setSelectedRouterRow('');
    setSelectedClickedDevice("")
    setUrlSearch("")
    if (routerOptionClicked) {
    setUrlClicked(false)
    }
    fetchApi();
  }, [props.selectedDate, dnsCheckBox, props.startDate, props.endDate, props.cpeId, props.deviceId, props.url, props.account]);

  useEffect(() => {
    if (dnsDataClicked && cpeSearch) {
      props.setMarker(true);
      setRouterClicked(true);
      setRouterOptionClicked(true);
      handleRowClick({ key: { 'CPE ID': cpeSearch } });
    }
    if (dnsDataClicked && deviceSearch) {
      props.setMarker(true);
      setRouterClicked(true);
      setRouterOptionClicked(true);
      setDeviceChartClicked(true);
    }
    if (dnsDataClicked && !cpeSearch && !deviceSearch) {
      getDeviceIds(dataClicked);
    }
  }, [dnsDataClicked]);

  const textArray = [
    t('activity.loading.onIt'),
    t('activity.loading.almostThere'),
    t('activity.loading.pleaseWait'),
    t('activity.loading.pullingLatest'),
  ];

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentText((prevText) => (prevText + 1) % textArray.length);
    }, 2000);

    return () => clearInterval(intervalId);
  }, [loading]);

  async function fetchApi() {
    setLoading(true);
    setError(false);
    let diff = new Date().getTimezoneOffset();
    const graphData = await axios
      .get(
        `/v2/op/dns-records/v3/dns-graph?operatorId=${operatorId}&l=${props.selectedDate}&type=${dnsCheckBox}&devId=${
          props.deviceId
        }&cpeId=${props.cpeId}&startDate=${props.startDate}&endDate=${props.endDate}&offset=${diff}&url=${
          props.url ? props.url : ''
        }&account=${props.account ? props.account : ''}`
      )
      .catch((err) => {
        setLoading(false);
        setError(true);
        setLineGraph((prevLineGraph) => ({
          ...prevLineGraph,
          series: [],
        }));
        setDnsBarChart((prevLineGraph) => ({
          ...prevLineGraph,
          series: [],
        }));
      });

    // need to add offset

    if (graphData) {
      updateGraphs(graphData.data.response[0].dnsData, graphData.data.response[0].trendData || []);
      setLimit(graphData?.data?.response[0]?.limit);
      setLoading(false);
    }
  }

  useEffect(() => {
    localStorage.setItem('limit', limit);
  }, [limit]);

  async function formatDate(datestring: string) {
    try {
      const date = new Date(datestring);

      let hours: string;
      let fullDate = datestring.split(' ')[0];

      const isHourlyFormat = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(datestring);
      const isDateFormat = /^\d{4}-\d{2}-\d{2}$/.test(datestring);

      if (isHourlyFormat) {
        hours = String(date.getHours() + 1).padStart(2, '0');
        setStartDate(`${datestring}.000`);
        setEndDate(`${fullDate} ${hours}:00:00.000`);
        return { startDate: `${datestring}.000`, endDate: `${fullDate} ${hours}:00:00.000` };
      }
      if (isDateFormat) {
        date.setDate(date.getDate() + 1);
        setStartDate(`${fullDate} 00:00:00.000`);
        setEndDate(`${date.toLocaleDateString('sv-SE')} 00:00:00.000`);
        return { startDate: `${fullDate} 00:00:00.000`, endDate: `${date.toLocaleDateString('sv-SE')} 00:00:00.000` };
      }
    } catch (e) {
      console.log(e);
    }
  }

  async function getDeviceIds(datestring: string) {
    try {
      formatDate(dataClicked);
      setLoading(true);
      const dateLimit = localStorage.getItem('limit');
      const requesters = await axios.get(
        `/v2/op/dns-records/dns-graph/devices?operatorId=${operatorId}&cpeId=${props.cpeId}&devId=${props.deviceId}&l=${dateLimit}&timestamp=${datestring}&type=${dnsCheckBox}`
      );
      updateRouterbarChart(datestring, requesters.data.response);
    } catch (error) {
      showToastMessage(t('activity.errors.fetchDeviceIds'));
      setLoading(false);
    }
  }

  async function updateRouterbarChart(datestring: string, deviceIds: any) {
    try {
      const dateLimit = localStorage.getItem('limit');
      const requesters = await axios.post(
        `/v2/op/dns-records/dns-haandle-distribution?operatorId=${operatorId}&timestamp=${datestring}&type=${dnsCheckBox}&l=${dateLimit}`,
        {
          devId: deviceIds,
        }
      );

      setRouterBarChart((prevBarChart) => ({
        ...prevBarChart,
        series: [
          {
            name: 'Routers',
            data: requesters.data.response,
          },
        ],
      }));

      setLoading(false);
      setDnsDataClicked(true);
      setRouterWarnClick('');
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  }

  function updateGraphs(graphData: any, trendData: any) {
    try {
      setLineGraph((prevLineGraph) => ({
        ...prevLineGraph,
        series: [
          ...(trendData && trendData.length > 0
            ? [
                {
                  name: 'Alert',
                  data: setRedLineData(trendData),
                },
                {
                  name: 'Warn',
                  data: setOrangeLineData(trendData),
                },
                {
                  name: 'Normal',
                  data: trendData,
                },
              ]
            : []),
          {
            name: 'DNS Requests',
            data: graphData,
          },
        ],
        options: {
          ...prevLineGraph.options,
          markers: {
            discrete: generateDiscreteMarkers(graphData, trendData),
            ...(trendData && trendData.length > 0
              ? {
                  size: [0, 0, 0, 0],
                }
              : {
                  size: [6],
                  // discrete: []
                }),
          },
          ...(trendData && trendData.length > 0
            ? {
                colors: ['#dc3545', '#FFBF00', '#22c55e', '#003D4C'],
              }
            : {
                colors: ['#003D4C'],
              }),
          fill: {
            ...(trendData && trendData.length > 0
              ? {
                  type: ['gradient', 'gradient', 'gradient', 'image'],
                }
              : {
                  type: ['image'],
                }),
            gradient: {
              opacityFrom: 0.6,
              opacityTo: 0.8,
            },
          },
          stroke: {
            width: [4, 4, 4, 4],
            curve: 'straight',
            dashArray: [0, 0],
          },
        },
      }));

      setDnsBarChart((prevDnsBarChart) => ({
        ...prevDnsBarChart,
        series: [
          {
            name: 'DNS Requests',
            data: formatData(graphData, trendData),
            // data: data
          },
          {
            name: 'DNS Trend',
            data: trendData,
          },
        ],
        options: {
          ...prevDnsBarChart.options,
          markers: {
            ...prevDnsBarChart.options.markers,
          },
          ...(trendData && trendData.length > 0
            ? {
                colors: ['#22c55e', '#003D4C'],
              }
            : {
                colors: ['#003D4C'],
              }),
        },
      }));
    } catch (err) {
      console.log(err);
    }
  }

  const [selectedRouterRow, setSelectedRouterRow] = useState<string>('');

  // endpoint will need to be changed or edited after trend is completed
  const handleRowClick = async (row: any) => {
    try {
      setSelectedRouterRow(row.key.Registrant);
      const time = await formatDate(dataClicked);
      const rowCpeId = row.key['CPE ID'];
      const dateLimit = localStorage.getItem('limit');
      setLoading(true);
      const response = await axios.get(
        `/v2/op/dns-records/dns-device-graph/${rowCpeId}?&startDate=${time?.startDate}&endDate=${time?.endDate}&type=${dnsCheckBox}&l=${dateLimit}`
      );
      setLoading(false);
      setColumnChart((prevColumnChart) => ({
        ...prevColumnChart,
        series: [
          {
            name: 'Normal',
            data: response.data.response[0].results.normal,
          },
          {
            name: 'Warn',
            data: response.data.response[0].results.warn,
          },
          {
            name: 'Alert',
            data: response.data.response[0].results.alert,
          },
          ...(response.data.response[0].results.unknown && response.data.response[0].results.unknown.length > 0
            ? [
                {
                  name: 'Unknown',
                  data: response.data.response[0].results.unknown,
                },
              ]
            : []),
        ],
        options: {
          ...prevColumnChart.options,
          labels: response.data.response[0].results.labels,
        },
      }));

      setPieChart((prevPieChart) => ({
        ...prevPieChart,
        series: response.data.response[0].pieData[0].numbers,
        options: {
          ...prevPieChart.options,
          labels: response.data.response[0].pieData[0].labels.length === 0 ? ['None'] : response.data.response[0].pieData[0].labels,
        },
      }));

      setRouterClicked(true);
    } catch (e) {
      console.error(e);
      setLoading(false);
      showToastMessage(t('activity.errors.accessFailed', { name: row.key['Registrant'] }));
    }
  };

  const handleUrlRowClick = async (row: any) => {
    setUrlSearch(row.key.FQDN);
    setRouterTitle('Routers');
    setUrlClicked(false);
    setUrlClicked(true);
    setSelectedCategory(row.key.categories);
  };

  const handleBubbleClick = async (clickedData: any) => {
    setChartDeviceIds(clickedData.devices);
    setDeviceChartClicked(true);
  };

  const handleCheckboxChange = (value: string) => {
    setDnsCheckBox(value);
  };

  const handleBackButton = () => {
    const resetState = () => {
      setRouterOptionClicked(false);
      setRouterClicked(false);
      setDeviceChartClicked(false);
      setDnsDataClicked(false);
      props.setMarker(false);
      setUrlClicked(false);
      setSelectedRouterRow('');
      setSelectedClickedDevice('');
      setUrlSearch('');
      setDataClicked('');
    };

    if (deviceChartClicked) {
      if (deviceSearch) {
        resetState();
      }
      setDeviceChartClicked(false);
      setUrlClicked(false);
      setSelectedClickedDevice('');
    } else if (routerClicked) {
      if (cpeSearch) {
        resetState();
      }
      setRouterClicked(false);
      setSelectedRouterRow('');
      setSelectedClickedDevice('');
      setUrlSearch('');
    } else if (urlClicked) {
      setUrlClicked(false);
      setUrlSearch('');
    } else if (routerOptionClicked) {
            setRouterWarnClick("")
      setRouterOptionClicked(false);
      setSelectedRouterRow('');
      setSelectedClickedDevice('');
      setUrlSearch('');
      props.setMarker(false);
    } else if (dnsDataClicked) {
      setDnsDataClicked(false);
      props.setMarker(false);
      setSelectedRouterRow('');
      setRouterWarnClick("")
      setSelectedClickedDevice('');
      setUrlSearch('');
      setDataClicked('');
    }
  };

  function setOrangeLineData(trendData: any) {
    // fixed percentage at the moment //
    try {
      return trendData.map((item: { x: any; y: number }) => ({
        x: item.x,
        y: Math.round(item.y + item.y * (warnPercentage / 100)),
      }));
    } catch (err) {
      console.log(err);
    }
  }

  function setRedLineData(trendData: any) {
    // fixed percentage at the moment //
    try {
      return trendData.map((item: { x: any; y: number }) => ({
        x: item.x,
        y: Math.round(item.y + item.y * (alertPercentage / 100)),
      }));
    } catch (err) {
      console.log(err);
    }
  }

  // for line graph //
  function generateDiscreteMarkers(series1: any, series2: any) {
    const discreteMarkers = [];

    try {
      for (let i = 0; i < series1.length; i++) {
        if (series2.length == 0) {
          discreteMarkers.push({
            seriesIndex: 3,
            dataPointIndex: i,
            fillColor: '#003D4C',
            strokeColor: '#ffffff',
            size: 6,
          });
        } else {
          if (series2[i]) {
            const percentageDiff = (series1[i].y - series2[i].y) / series2[i].y;

            if (series1[i].y <= series2[i].y) {
              discreteMarkers.push({
                seriesIndex: 3,
                dataPointIndex: i,
                fillColor: '#22c55e',
                strokeColor: '#ffffff',
                size: 6,
              });
            } else {
              if (percentageDiff >= warnPercentage / 100) {
                discreteMarkers.push({
                  seriesIndex: 3,
                  dataPointIndex: i,
                  fillColor: '#dc3545',
                  strokeColor: '#ffffff',
                  size: 6,
                });
              } else {
                //orange
                discreteMarkers.push({
                  seriesIndex: 3,
                  dataPointIndex: i,
                  fillColor: '#FFBF00',
                  strokeColor: '#ffffff',
                  size: 6,
                });
              }
            }
          } else {
            discreteMarkers.push({
              seriesIndex: 3,
              dataPointIndex: i,
              fillColor: '#003D4C',
              strokeColor: '#ffffff',
              size: 6,
            });
          }
        }
      }
      return discreteMarkers;
    } catch (err) {
      console.log(err);
    }
  }

  // for bar chart //
  function formatData(series1: any, series2: any) {
    const todaysData = [];
    try {
      for (let i = 0; i < series1.length; i++) {
        const percentageDiff = (series1[i].y - series2[i].y) / series2[i].y;

        if (series1[i].y < series2[i].y) {
          todaysData.push({
            x: series1[i].x,
            y: series1[i].y,
            time_group: series1[i].time_group,
            devices: series1[i].devices,
            fillColor: '#22c55e',
          });
        } else {
          if (percentageDiff > warnPercentage / 100) {
            todaysData.push({
              x: series1[i].x,
              y: series1[i].y,
              time_group: series1[i].time_group,
              devices: series1[i].devices,
              fillColor: '#dc3545',
            });
          } else {
            todaysData.push({
              x: series1[i].x,
              y: series1[i].y,
              time_group: series1[i].time_group,
              devices: series1[i].devices,
              fillColor: '#FFBF00',
            });
          }
        }
      }

      return todaysData;
    } catch (err) {
      return series1;
    }
  }

  const [lineGraph, setLineGraph] = useState({
    series: [
      {
        name: 'DNS Requests',
        data: [],
      },
    ],
    options: {
      chart: {
        height: 350,
        type: 'line',
        zoom: {
          enabled: false,
        },
        events: {
          dataPointSelection: (event, chartContext, opts) => {
            const { seriesIndex, dataPointIndex, w } = opts;
            const clickedData = w.config.series[seriesIndex].data[dataPointIndex];

            setDataClicked(clickedData.time_group);
            setDnsDataClicked(true);
            setRouterWarnClick('');
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
      tooltip: {
        enabled: false,
      },
      grid: {
        borderColor: '#f1f1f1',
      },
      yaxis: {
        title: {
          text: t('activity.series.dnsActivity'),
        },
      },
    } as ApexOptions,
  });

  const [dnsBarChart, setDnsBarChart] = useState({
    series: [
      {
        name: 'DNS Requests',
        data: [],
      },
      {
        name: 'DNS Trend',
        data: [],
      },
    ],
    options: {
      chart: {
        type: 'bar',
        height: 350,
        events: {
          dataPointSelection: (event, chartContext, opts) => {
            const { seriesIndex, dataPointIndex, w } = opts;
            const clickedData = w.config.series[seriesIndex].data[dataPointIndex];

            setDataClicked(clickedData.time_group);
            setDnsDataClicked(true);
            setRouterWarnClick('');
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          endingShape: 'rounded',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      colors: ['#22c55e', '#003D4C'],
      fill: {
        opacity: 1,
      },
      yaxis: {
        title: {
          text: t('activity.series.dnsActivity'),
        },
      },
    } as ApexOptions,
  });

  const [routerWarnClick, setRouterWarnClick] = useState<string>('');

  const [routerBarChart, setRouterBarChart] = useState({
    series: [
      {
        name: 'Routers',
        data: [],
      },
    ],
    options: {
      chart: {
        type: 'bar',
        height: 350,
        events: {
          dataPointSelection: (event, chartContext, opts) => {
            const { seriesIndex, dataPointIndex, w } = opts;
            const clickedData = w.config.series[seriesIndex].data[dataPointIndex];
            setRequestersIds(clickedData.cpe);
            setRouterWarnClick(clickedData.x);
            setRouterOptionClicked(true);
            props.setMarker(true);
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          endingShape: 'rounded',
        },
      },
      noData: {
        text: t('activity.graphs.noData'),
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: 0,
        style: {
          color: '#263238',
          fontSize: '20px',
          fontFamily: undefined,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      yaxis: {
        title: {
          text: t('activity.series.routers'),
        },
      },
      colors: [
        function ({ seriesIndex, dataPointIndex, w }: { seriesIndex: any; dataPointIndex: number; w: any }) {
          const clickedData = w.config.series[seriesIndex].data[dataPointIndex];
          if (clickedData?.x === 'Normal') return '#22c55e';
          if (clickedData?.x === 'Warn') return '#FFBF00';
          if (clickedData?.x === 'Alert') return '#dc3545';
          if (clickedData?.x === 'Unknown') return '#707070';
          return '#000';
        },
      ],
    } as ApexOptions,
  });

  const [pieChart, setPieChart] = useState({
    series: [1],
    options: {
      chart: {
        type: 'donut',
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
        },
      },
      title: {
        text: t('activity.graphs.deviceDistribution'),
        align: 'left',
        margin: 10,
        offsetX: 0,
        offsetY: 0,
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          fontFamily: undefined,
          color: '#263238',
        },
      },
      subtitle: {
        text: t('activity.graphs.deviceDistributionSubtitle'),
        align: 'left',
        margin: 50,
        offsetX: 0,
        offsetY: 0,
        style: {
          fontSize: '14px',
          fontWeight: 'normal',
          fontFamily: undefined,
          color: '#9699a2',
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
    } as ApexOptions,
  });

  const [selectedClickedDevice, setSelectedClickedDevice] = useState<string>('');

  const [columnChart, setColumnChart] = useState({
    series: [
      {
        data: [],
      },
    ],
    options: {
      chart: {
        type: 'bar',
        height: 350,
        stacked: true,
        stackType: '100%',
        events: {
          dataPointSelection: (event, chartContext, opts) => {
            const { seriesIndex, dataPointIndex, w } = opts;
            const clickedData = w.config.series[seriesIndex].data[dataPointIndex];
            setSelectedClickedDevice(clickedData.x);
            handleBubbleClick(clickedData);
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: 'bottom',
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],
      colors: ['#22c55e', '#FFBF00', '#dc3545', '#707070'],
      fill: {
        opacity: 1,
      },
      legend: {
        position: 'right',
        offsetX: 0,
        offsetY: 50,
      },
    } as ApexOptions,
  });

  // Chart texts are re-applied at render time so they follow language changes
  const lineGraphOptions = useMemo(
    () => ({ ...lineGraph.options, yaxis: { ...(lineGraph.options.yaxis as any), title: { text: t('activity.series.dnsActivity') } } } as ApexOptions),
    [lineGraph.options, i18n.language]
  );
  const dnsBarChartOptions = useMemo(
    () => ({ ...dnsBarChart.options, yaxis: { ...(dnsBarChart.options.yaxis as any), title: { text: t('activity.series.dnsActivity') } } } as ApexOptions),
    [dnsBarChart.options, i18n.language]
  );
  const routerBarChartOptions = useMemo(
    () =>
      ({
        ...routerBarChart.options,
        noData: { ...routerBarChart.options.noData, text: t('activity.graphs.noData') },
        yaxis: { ...(routerBarChart.options.yaxis as any), title: { text: t('activity.series.routers') } },
        xaxis: { ...routerBarChart.options.xaxis, labels: { ...routerBarChart.options.xaxis?.labels, formatter: (value: string) => seriesLabel(value) } },
        tooltip: { ...routerBarChart.options.tooltip, x: { formatter: (value: any) => seriesLabel(String(value)) } },
      } as ApexOptions),
    [routerBarChart.options, i18n.language]
  );
  const pieChartOptions = useMemo(
    () =>
      ({
        ...pieChart.options,
        labels: pieChart.options.labels?.map((label: string) => (label === 'None' ? t('activity.graphs.none') : label)),
        title: { ...pieChart.options.title, text: t('activity.graphs.deviceDistribution') },
        subtitle: { ...pieChart.options.subtitle, text: t('activity.graphs.deviceDistributionSubtitle') },
      } as ApexOptions),
    [pieChart.options, i18n.language]
  );

  const LoadingOverlay = (
    <div className="absolute inset-0 flex items-center justify-center z-10 bg-gray-400 bg-opacity-10 rounded-xl">
      <span key={currentText} className="text-black text-2xl animate-[bounce_2s_ease-in-out_infinite]">
        {textArray[currentText]}
      </span>
    </div>
  );

  const ErrorOverlay = (
    <div className="absolute inset-0 flex items-center justify-center z-10 bg-gray-400 bg-opacity-10 rounded-xl text-red-500 text-2xl">
      {cpeSearch && !deviceSearch ? <span>{t('activity.errors.cpeNotFound')}</span> : null}
      {cpeSearch && deviceSearch ? (
        <div className="flex-col flex items-center justify-center">
          <span>{t('activity.errors.deviceNotFound')}</span>
          <span>{t('activity.errors.deviceNotUnderCpe')}</span>
        </div>
      ) : null}
      {!cpeSearch && deviceSearch ? <span>{t('activity.errors.deviceNotFound')}</span> : null}
    </div>
  );

  // const dnsCards: any = [];

  // if (dnsDataClicked) {
  //   dnsCards.push({
  //     label: `DNS data for ${startDate}`,
  //     onBreadcrumbClick: () => {
  //       setRouterOptionClicked(false);
  //       setRouterClicked(false);
  //       setDeviceChartClicked(false);
  //       setDnsDataClicked(false);
  //       props.setMarker(false);
  //       setUrlClicked(false);
  //     },
  //   });
  // }

  // if (routerOptionClicked) {
  //   dnsCards.push({
  //     label: `${routerWarnClick} Routers`,
  //     onBreadcrumbClick: () => handleBackButton(),
  //   });
  // }

  // if (routerClicked) {
  //   dnsCards.push({
  //     label: `${selectedRouterRow}`,
  //     onBreadcrumbClick: () => {
  //       handleBackButton();
  //     },
  //   });
  // }

  // if (deviceChartClicked) {
  //   dnsCards.push({
  //     label: `${selectedClickedDevice}`,
  //     onBreadcrumbClick: () => {
  //       handleBackButton();
  //     },
  //   });

  //   //  dnsCards.push({
  //   //   label: `Device List`,
  //   //   onBreadcrumbClick: () => {
  //   //   },
  //   // });
  // }

  // if (urlClicked) {
  //   dnsCards.push({ label: urlSearch, onBreadcrumbClick: () =>       setUrlClicked(false) });
  // }

  const [routerTitle, setRouterTitle] = useState<string>('');

  useEffect(() => {
    setUrlSearch('');
    setDataClicked('');
    setSelectedClickedDevice('');
  }, [props.cpeId, props.deviceId]);

  const [dnsCards, setDnsCards] = useState<any[]>([]);

  useEffect(() => {
    const initialCrumbs: any[] = [
      {
        label: `${dataClicked || ''}`,
        onBreadcrumbClick: () => {
          setUrlSearch('');
          setDataClicked('');
          setRouterWarnClick('');
          setSelectedRouterRow('');
          setSelectedClickedDevice('');
          setRouterOptionClicked(false);
          setRouterClicked(false);
          setDeviceChartClicked(false);
          setDnsDataClicked(false);
          props.setMarker(false);
          setUrlClicked(false);
        },
      },
    ];

    if (props.cpeId !== '' || props.deviceId !== '') {
    } else {
      if (dnsDataClicked) {
        initialCrumbs.push({
          label: `${seriesLabel(routerWarnClick)}`,
          onBreadcrumbClick: () => {
            setRouterWarnClick("")
            setSelectedRouterRow('');
            setSelectedClickedDevice('');
            setRouterOptionClicked(false);
            setDeviceChartClicked(false);
            setRouterClicked(false);
            props.setMarker(false);
            setUrlSearch('');

            if (deviceChartClicked) {
              if (deviceSearch) {
                setSelectedRouterRow('');
                setSelectedClickedDevice('');
                setRouterOptionClicked(false);
                setDeviceChartClicked(false);
                setRouterClicked(false);
                props.setMarker(false);
              }
              setDeviceChartClicked(false);
              setUrlClicked(false);
            } else if (routerClicked) {
              if (cpeSearch) {
                setSelectedRouterRow('');
                setSelectedClickedDevice('');
                setRouterOptionClicked(false);
                setDeviceChartClicked(false);
                setRouterClicked(false);
                props.setMarker(false);
              }
              setRouterClicked(false);
            } else if (urlClicked) {
              setUrlClicked(false);
            } else if (routerOptionClicked) {
              setRouterOptionClicked(false);
              props.setMarker(false);
            } else if (dnsDataClicked) {
              setDnsDataClicked(false);
              props.setMarker(false);
            }
          },
        });
      }

      if (routerOptionClicked) {
        if (urlClicked && !routerClicked) {
          initialCrumbs.push({
            label: routerTitle,
            onBreadcrumbClick: () => {
              setUrlSearch('');
              setUrlClicked(false);
            },
          });

          initialCrumbs.push({
            label: urlSearch,
            onBreadcrumbClick: () => {},
          });
        } else {
          initialCrumbs.push({
            label: `${selectedRouterRow}`,
            onBreadcrumbClick: () => {
              setSelectedRouterRow('');
              setDeviceChartClicked(false);
              setSelectedClickedDevice('');
              setUrlSearch('');

              if (deviceSearch) {
                setSelectedRouterRow('');
                setSelectedClickedDevice('');
                setRouterOptionClicked(false);
                setDeviceChartClicked(false);
                setRouterClicked(false);
                props.setMarker(false);
              }
              setDeviceChartClicked(false);
              setUrlClicked(false);

              if (routerClicked) {
                if (cpeSearch) {
                }
                setRouterClicked(false);
              } else if (urlClicked) {
                setUrlClicked(false);
              } else if (routerOptionClicked) {
                setRouterOptionClicked(false);
                props.setMarker(false);
              } else if (dnsDataClicked) {
                setDnsDataClicked(false);
                props.setMarker(false);
              }
            },
          });
        }
      }
    }

    if (routerClicked) {
      initialCrumbs.push({
        label: `${selectedClickedDevice}`,
        onBreadcrumbClick: () => {
          setUrlSearch('');
          setSelectedClickedDevice('');
          if (deviceChartClicked) {
            if (deviceSearch) {
              setSelectedRouterRow('');
              setSelectedClickedDevice('');
              setRouterOptionClicked(false);
              setDeviceChartClicked(false);
              setRouterClicked(false);
              props.setMarker(false);
            }
            setDeviceChartClicked(false);
            setUrlClicked(false);
          } else if (routerClicked) {
            if (cpeSearch) {
              setSelectedRouterRow('');
              setSelectedClickedDevice('');
              setRouterOptionClicked(false);
              setDeviceChartClicked(false);
              setRouterClicked(false);
              props.setMarker(false);
            }
            setRouterClicked(false);
          } else if (urlClicked) {
            setUrlClicked(false);
          } else if (routerOptionClicked) {
            setRouterOptionClicked(false);
            props.setMarker(false);
          } else if (dnsDataClicked) {
            setDnsDataClicked(false);
            props.setMarker(false);
          }
        },
      });

      if (deviceChartClicked && !urlClicked) {
        initialCrumbs.push({
          label: "",
          onBreadcrumbClick: () => setSelectedClickedDevice(""),
        });
      }
    }

    if (urlClicked && routerClicked) {
      initialCrumbs.push({
        label: urlSearch,
        onBreadcrumbClick: () => setUrlClicked(false),
      });
    }

    setDnsCards(initialCrumbs);
  }, [
    startDate,
    endDate,
    dnsDataClicked,
    routerWarnClick,
    routerOptionClicked,
    selectedRouterRow,
    routerClicked,
    selectedClickedDevice,
    deviceChartClicked,
    urlSearch,
    props.deviceId,
    props.cpeId,
    i18n.language,
  ]);

  return (
    <section className="rounded-2xl shadow-md">
      <div className="absolute top-[320px] left-[35px]">
        <CustomBreadcrumbs
          paths={dnsCards}
          onTrim={(index) => {
            setDnsCards((prev) => prev.slice(0, index + 1));
          }}
        />
      </div>

      <div className={`${routerOptionClicked ? 'mt-5 grid grid-cols-4 gap-8' : ''} `}>
        {!deviceChartClicked && (
          <>
            {routerOptionClicked && (
              <div style={{ borderRadius: 16 }} className="retina-table col-span-2 p-4 mt-5">
                {/* Router Fqdn's */}
                {!routerClicked ? (
                  <>
                    <div className="items-center grid h-[55px]">
                      <span className="text-xl">{t('activity.graphs.activityFor', { name: operatorNameArray?.name ?? operatorId })}</span>
                    </div>
                    <div className="rounded-2xl shadow-md">
                      <TableWidget
                        hasSearch={true}
                        url={`v2/op/dns-records/${operatorId}/list-records`}
                        countPerPage={5}
                        title={t('activity.graphs.routerDnsRecords')}
                        searchPositionRight={true}
                        onRowSelect={handleUrlRowClick}
                        cpeId={requestersIds}
                        deviceId={[]}
                        startDate={startDate}
                        endDate={endDate}
                        isDns={true}
                        needCategories={true}
                        type={dnsCheckBox}
                        urlSearch={props.url}
                        selectedDate={props.selectedDate}
                        hasCategoryFilter={true}
                      />
                    </div>
                  </>
                ) : (
                  <ReactApexChart options={pieChartOptions} series={pieChart.series} type="donut" height={425} />
                )}
              </div>
            )}
          </>
        )}

        {deviceChartClicked && (
          <div style={{ borderRadius: 16 }} className="retina-table col-span-2 p-4">
            <div className="items-center grid h-[55px]">
              <span className="text-xl">{t('activity.graphs.activityFor', { name: operatorNameArray?.name ?? operatorId })}</span>
            </div>
            {/* Device Fqdn's */}
            <div className="rounded-2xl shadow-md">
              <TableWidget
                hasSearch={true}
                url={`v2/op/dns-records/${operatorId}/list-records`}
                countPerPage={8}
                title={t('activity.graphs.deviceDnsRecords')}
                searchPositionRight={true}
                onRowSelect={handleUrlRowClick}
                deviceId={deviceSearch ? [deviceSearch] : chartDeviceIds}
                startDate={startDate}
                endDate={endDate}
                isDns={true}
                needCategories={true}
                type={dnsCheckBox === 'BLOCKED' ? 'TIMESTAMPBLOCKED' : 'TIMESTAMP'}
                urlSearch={props.url}
                selectedDate={props.selectedDate}
                hasCategoryFilter={true}
              />
            </div>
          </div>
        )}

        <div style={{ borderRadius: 16 }} className="retina-table col-span-2 p-4">
          <div className={`items-center grid ${dnsDataClicked ? 'flex h-[55px]' : 'grid-cols-3'}`}>
            {dnsDataClicked && (
              <img src="/icons/arrow-prev-pag.svg" className="hover:cursor-pointer mr-10" onClick={handleBackButton} />
            )}
            <span className="text-xl">{t('activity.graphs.activityFor', { name: operatorNameArray?.name ?? operatorId })}</span>

            {!dnsDataClicked && (
              <>
                <div className="flex justify-center dnsg-switch" role="group" aria-label={t('activity.graphs.chartType')}>
                  <button
                    type="button"
                    aria-pressed={graphSwitch === true}
                    onClick={() => {
                      setGraphSwitch(true);
                    }}
                    className={`${
                      graphSwitch === true ? 'bg-green-500 text-white active' : 'bg-gray-300 text-gray-800'
                    } rounded-l-xl px-4 py-2 w-20 `}
                  >
                    <span className="fonts">{t('activity.graphs.line')}</span>
                  </button>
                  <button
                    type="button"
                    aria-pressed={graphSwitch === false}
                    onClick={() => {
                      setGraphSwitch(false);
                    }}
                    className={`${
                      graphSwitch === false ? 'bg-green-500 text-white active' : 'bg-gray-300 text-gray-800'
                    } rounded-r-xl px-4 py-2 w-20`}
                  >
                    <span className="fonts">{t('activity.graphs.bar')}</span>
                  </button>
                </div>
                <div>
                  <div className="flex items-center justify-center mr-6">
                    <input
                      type="checkbox"
                      id="24-hours"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      onChange={() => handleCheckboxChange('DNS')}
                      checked={dnsCheckBox == 'DNS'}
                    />
                    <label className="ml-2 text-sm text-gray-900">{t('activity.graphs.dnsRequests')}</label>
                  </div>

                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      id="7-days"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      onChange={() => handleCheckboxChange('BLOCKED')}
                      checked={dnsCheckBox == 'BLOCKED'}
                    />
                    <label className="ml-2 text-sm text-gray-900">{t('activity.graphs.blockedRequests')}</label>
                  </div>
                  <div className="flex items-center justify-center mr-7">
                    <input
                      type="checkbox"
                      id="30-days"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      onChange={() => handleCheckboxChange('UNIQUE')}
                      checked={dnsCheckBox == 'UNIQUE'}
                    />
                    <label className="ml-2 text-sm text-gray-900">{t('activity.graphs.uniqueUrls')}</label>
                  </div>
                </div>
              </>
            )}
          </div>
          {!dnsDataClicked && (
            <>
              {graphSwitch && (
                <div className="relative">
                  {loading ? LoadingOverlay : ''}
                  {error ? ErrorOverlay : ''}
                  <div className={`${loading ? 'blur-[2px]' : ''}`}>
                    <ReactApexChart options={lineGraphOptions} series={localizeSeries(lineGraph.series)} type="area" height={425} />
                  </div>
                </div>
              )}
              {!graphSwitch && (
                <div className="relative">
                  {loading ? LoadingOverlay : ''}
                  {error ? ErrorOverlay : ''}
                  <div className={`${loading ? 'blur-[2px]' : ''}`}>
                    <ReactApexChart options={dnsBarChartOptions} series={localizeSeries(dnsBarChart.series)} type="bar" height={425} />
                  </div>
                </div>
              )}
            </>
          )}

          {dnsDataClicked && (
            <>
              {!routerOptionClicked && (
                <div className="relative">
                  {loading ? LoadingOverlay : ''}
                  <div className={`${loading ? 'blur-[2px]' : ''}`}>
                    <ReactApexChart options={routerBarChartOptions} series={localizeSeries(routerBarChart.series)} type="bar" height={425} />
                  </div>
                </div>
              )}

              {!deviceChartClicked && (
                <>
                  {routerClicked && (
                    <div className="relative">
                      {loading ? LoadingOverlay : ''}
                      <div className={`${loading ? 'blur-[2px]' : ''}`}>
                        <ReactApexChart options={columnChart.options} series={localizeSeries(columnChart.series)} type="bar" height={425} />
                      </div>
                    </div>
                  )}
                </>
              )}

              {!routerClicked && (
                <>
                  {routerOptionClicked && (
                    // list of all routers in bar chart
                    <div className="relative">
                      {loading ? LoadingOverlay : ''}
                      <div className={`${loading ? 'blur-[2px]' : ''}`}>
                        {!urlClicked && (
                          <div className="rounded-2xl shadow-md">
                            <TableWidget
                              hasSearch={true}
                              url={`v2/op/account/accounts-by-devices`}
                              countPerPage={8}
                              title={t('activity.graphs.routers')}
                              searchPositionRight={true}
                              onRowSelect={handleRowClick}
                              cpeId={requestersIds}
                              isDns={true}
                              needCategories={true}
                            />
                          </div>
                        )}
                        {urlClicked && (
                          // list of all dns for selected url
                          <div className="rounded-2xl shadow-md">
                            <TableWidget
                              hasSearch={true}
                              url={`v2/op/dns-records/${operatorId}/list-records`}
                              countPerPage={8}
                              title={t('activity.graphs.routerDnsRecords')}
                              searchPositionRight={true}
                              cpeId={requestersIds}
                              deviceId={[]}
                              startDate={startDate}
                              endDate={endDate}
                              isDns={true}
                              needCategories={true}
                              type={dnsCheckBox === 'BLOCKED' ? 'TIMESTAMPBLOCKED' : 'TIMESTAMP'}
                              urlSearch={urlSearch}
                              key={urlSearch}
                              countSearch={'operator'}
                              selectedDate={props.selectedDate}
                              category={selectedCategory !== '' ? selectedCategory : ''}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {deviceChartClicked && (
                // list of all device in % chart selected
                <div className="rounded-2xl shadow-md">
                  <TableWidget
                    hasSearch={true}
                    url={`v2/op/device/list-devices-by-deviceIds`}
                    countPerPage={8}
                    title={t('activity.graphs.devices')}
                    searchPositionRight={true}
                    // onRowSelect={handleRowClick}
                    deviceId={deviceSearch ? [deviceSearch] : chartDeviceIds}
                    isDns={true}
                    needCategories={true}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};
